/*
  SourceTaggingCard.jsx (v1.7, replaces v1.6.2)
  Generosity, Inc. | WTR App | WTR BTL page

  Bottom sheet rendered by WTRBottleScreen after a test completes.
  Flow: suggest check (4 s auto-confirm for graduated users, fails
  open to the grid), ten-pill grid, Bottled brand drawer (eleven
  brands plus free text), rotating insight line, and the Trust But
  Verify bridge into BottleScanView via onOpenScanner.

  v1.7 CHANGES FROM v1.6.2 (root-cause fix for hollow label rows):
  - PROP CONTRACT NOW MATCHES WTRBottleScreen AND
    sourceTagging.config v1.7. The v1.6.2 card expected liveReading,
    testId, onTagged, onOpenBrandReport; the parent passes test,
    onTag, onOpenScanner. Result in production: liveReading defaulted
    to null, so the card's own POST inserted rows with tds and score
    null, and the parent's tagSource (which posts the CORRECT
    tds/score) never fired because onTagged was never wired. This
    file adopts the parent's contract verbatim.
  - SINGLE WRITE PATH. The card no longer POSTs. It calls
    onTag(source, brand, attributionMode); WTRBottleScreen.tagSource
    owns the one POST to /api/bottle/test-source with tds and score
    from the pinned history row. This also removes the double-insert
    that would have occurred had the props been aligned around two
    live POST paths.
  - buildBridge is called with its real signature
    (brand, hasLabelData). v1.6.2 passed four args, so hasLabelData
    received the TDS number and the "compare" copy rendered even for
    brands with no label data, violating the evidence-class honesty
    rule. Fixed: the compare branch renders only when the brand key
    exists in SCAN_BRAND_KEYS.
  - Bridge CTAs dispatch onOpenScanner(brandKey, live). "compare"
    passes { compare: true, tds, testId } as the live payload;
    "report" passes null. Known gap, not this file's scope:
    BottleScanView does not yet consume liveComparison, so compare
    currently opens the plain report. The payload is passed so the
    Measured-vs-Label strip can land later without touching this
    card.

  Verified contracts (July 2026 repo):
  - WTRBottleScreen renders:
      <SourceTaggingCard
        apiBase={API_BASE}
        test={lastTest}
        onTag={(source, brand, attributionMode) => tagSource(...)}
        onOpenScanner={(brandKey, live) => onOpenBottleScan(brandKey, live)}
        onClose={() => setSourcePromptTs(null)}
      />
  - test row shape: { ts, tds, score, source, ph, tempF } — tds is
    always a real positive number when this card opens, because
    WTRBottleScreen only logs a test entry on a real median reading.
  - GET /api/bottle/test-source/suggest?email=&tds= returns
    { mode: "prompt" } or { mode: "suggest", source, brand, confidence }.
  - Brand ids equal BOTTLE_BRANDS keys in BottleScanView.js;
    hasLabelData is membership in SCAN_BRAND_KEYS exported there.

  Props:
    apiBase        API base, same dialect as the rest of the app
    test           { ts, tds, score, ... } pinned test history row
    onTag          (source, brand, attributionMode) => void
                   Parent writes local history AND fires the single
                   training POST. The card never POSTs the label.
    onOpenScanner  (brandKey, live) => void
    onClose        dismiss handler
*/

import React, { useEffect, useRef, useState } from "react";
import { authHeaders } from "../lib/auth";
import { readProfile } from "../lib/profile";
import SourceTagging from "../lib/sourceTagging.config";
import { SCAN_BRAND_KEYS } from "./BottleScanView";

const COLORS = {
  white: "#FFFFFF",
  blue: "#51B0E6",
  gray: "#A6A8AB",
  light: "#F0F1F3",
  ink: "#232A33",
  amber: "#C7A230",
  blueSoft: "#EAF5FC",
  green: "#2E7D5B",
  greenSoft: "#EAF4EF",
};

const SCAN_DB_KEYS = new Set(SCAN_BRAND_KEYS);

function getEmail() {
  const email = ((readProfile().email) || "").trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null;
}

function CloseX({ onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label="Close"
      style={{ border: "none", background: "transparent", cursor: "pointer", padding: 6, flexShrink: 0 }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={COLORS.gray} strokeWidth="2.5" strokeLinecap="round">
        <path d="M6 6l12 12M18 6L6 18" />
      </svg>
    </button>
  );
}

function InsightLine({ text }) {
  if (!text) return null;
  return (
    <p
      style={{
        margin: "12px 0 0",
        padding: "8px 10px",
        background: COLORS.light,
        borderRadius: 10,
        fontSize: 11,
        lineHeight: 1.5,
        color: COLORS.gray,
        fontFamily: "'Barlow Condensed', 'Montserrat', sans-serif",
        letterSpacing: "0.02em",
      }}
    >
      {text}
    </p>
  );
}

/* Trust But Verify bridge card, rendered after a Bottled brand tag.
   CTAs pass the action name ("compare" | "report") up; the card maps
   actions to onOpenScanner with the right live payload. */
function BridgeCard({ bridge, onAction }) {
  return (
    <div style={{ background: COLORS.blueSoft, borderRadius: 14, padding: "14px 14px 12px", marginTop: 12 }}>
      <div style={{ fontSize: 14.5, fontWeight: 700, color: COLORS.ink, marginBottom: 4 }}>{bridge.headline}</div>
      <p style={{ margin: "0 0 10px", fontSize: 12, lineHeight: 1.5, color: COLORS.ink }}>{bridge.body}</p>
      {bridge.primaryCta ? (
        <button
          onClick={() => onAction(bridge.primaryCta.action)}
          style={{
            display: "block",
            width: "100%",
            border: "none",
            cursor: "pointer",
            fontFamily: "inherit",
            textAlign: "center",
            background: COLORS.blue,
            color: COLORS.white,
            fontSize: 12.5,
            fontWeight: 700,
            padding: "10px 12px",
            borderRadius: 999,
          }}
        >
          {bridge.primaryCta.label}
        </button>
      ) : null}
      {bridge.secondaryCta ? (
        <button
          onClick={() => onAction(bridge.secondaryCta.action)}
          style={{
            display: "block",
            width: "100%",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            fontFamily: "inherit",
            textAlign: "center",
            color: COLORS.blue,
            fontSize: 12,
            fontWeight: 700,
            padding: "9px 12px 2px",
          }}
        >
          {bridge.secondaryCta.label}
        </button>
      ) : null}
    </div>
  );
}

export default function SourceTaggingCard({
  apiBase = "",
  test = null,
  onTag = () => {},
  onOpenScanner = () => {},
  onClose = () => {},
}) {
  /* phase: checking | suggest | grid | drawer | done */
  const [phase, setPhase] = useState("checking");
  const [suggestion, setSuggestion] = useState(null);
  const [countdown, setCountdown] = useState(SourceTagging.CONFIRM_TIMEOUT_MS / 1000);
  const [insight, setInsight] = useState(null);
  const [bridge, setBridge] = useState(null);
  const [bridgeBrandKey, setBridgeBrandKey] = useState(null);
  const [doneLabel, setDoneLabel] = useState(null);
  const [otherBrand, setOtherBrand] = useState("");
  const submittedRef = useRef(false);
  const timersRef = useRef([]);

  const tds = test && test.tds != null ? test.tds : null;
  const testId = test && test.ts != null ? test.ts : null;

  const labelDataLookup = (brandId) => SCAN_DB_KEYS.has(brandId);

  function clearTimers() {
    timersRef.current.forEach((t) => clearInterval(t));
    timersRef.current = [];
  }

  /* Single write path: delegate to the parent. WTRBottleScreen.tagSource
     writes local history and fires the one training POST with the test
     row's tds and score. The card never POSTs the label itself; two live
     POST paths for the same tag means duplicate training rows. */
  function submitTag(source, brand, attributionMode) {
    if (submittedRef.current) return;
    submittedRef.current = true;
    clearTimers();

    onTag(source, brand || null, attributionMode);

    setInsight(SourceTagging.recordTagAndGetInsight());
    if (source === "Bottled" && brand) {
      const known = SourceTagging.BOTTLED_BRANDS.find((b) => b.id === brand);
      const brandObj = known || { id: brand, label: brand.charAt(0).toUpperCase() + brand.slice(1) };
      /* buildBridge(brand, hasLabelData) — real signature. The compare
         branch renders only when the scanner database actually holds
         Label Data for this brand. Never fabricate a comparison. */
      setBridge(SourceTagging.buildBridge(brandObj, labelDataLookup(brandObj.id)));
      setBridgeBrandKey(brandObj.id);
      setDoneLabel(brandObj.label);
    } else {
      setDoneLabel(source);
    }
    setPhase("done");
  }

  /* Bridge action dispatch. "compare" carries the live reading so
     BottleScanView can render Measured vs Label Data once it consumes
     liveComparison; "report" opens the plain brand report. */
  function handleBridgeAction(action) {
    if (!bridgeBrandKey) return;
    if (action === "compare") {
      onOpenScanner(bridgeBrandKey, { compare: true, tds, testId });
    } else {
      onOpenScanner(bridgeBrandKey, null);
    }
  }

  /* Suggest check on open. Fails open to the full grid. */
  useEffect(() => {
    let cancelled = false;
    async function check() {
      const email = getEmail();
      if (!email || tds == null) {
        setPhase("grid");
        return;
      }
      try {
        const params = new URLSearchParams({ email, tds: String(tds) });
        const res = await fetch(`${apiBase}/api/bottle/test-source/suggest?${params.toString()}`, {
          headers: authHeaders(),
        });
        if (!res.ok) throw new Error(String(res.status));
        const json = await res.json();
        if (cancelled) return;
        if (json && json.mode === "suggest" && json.source) {
          setSuggestion(json);
          setPhase("suggest");
        } else {
          setPhase("grid");
        }
      } catch (e) {
        if (!cancelled) setPhase("grid");
      }
    }
    check();
    return () => {
      cancelled = true;
      clearTimers();
    };
  }, []);

  /* 4 second auto-confirm countdown while in suggest phase. */
  useEffect(() => {
    if (phase !== "suggest" || !suggestion) return undefined;
    const started = Date.now();
    const t = setInterval(() => {
      const left = SourceTagging.CONFIRM_TIMEOUT_MS - (Date.now() - started);
      if (left <= 0) {
        submitTag(suggestion.source, suggestion.brand, SourceTagging.ATTRIBUTION_MODES.AUTO);
      } else {
        setCountdown(Math.ceil(left / 1000));
      }
    }, 250);
    timersRef.current.push(t);
    return () => clearInterval(t);
  }, [phase, suggestion]);

  const sheet = {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 60,
    background: COLORS.white,
    borderRadius: "20px 20px 0 0",
    padding: "16px 18px 20px",
    boxShadow: "0 -4px 24px rgba(35,42,51,0.16)",
    fontFamily: "'Montserrat', 'Helvetica Neue', sans-serif",
    color: COLORS.ink,
    maxHeight: "78vh",
    overflowY: "auto",
  };

  const pill = (selected) => ({
    border: `1.5px solid ${selected ? COLORS.blue : "#E4E7EA"}`,
    background: selected ? COLORS.blueSoft : COLORS.white,
    borderRadius: 14,
    padding: "10px 6px",
    cursor: "pointer",
    fontFamily: "inherit",
    textAlign: "center",
    width: "100%",
  });

  const header = (title, sub) => (
    <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 10 }}>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: COLORS.gray,
          }}
        >
          {title}
        </div>
        {sub ? <div style={{ fontSize: 13.5, fontWeight: 700, marginTop: 3 }}>{sub}</div> : null}
      </div>
      <CloseX onClick={onClose} />
    </div>
  );

  /* ---------- checking ---------- */
  if (phase === "checking") {
    return (
      <div style={sheet} aria-busy="true">
        {header("Source Check")}
        <div style={{ height: 44, background: COLORS.light, borderRadius: 12 }} />
      </div>
    );
  }

  /* ---------- suggest (graduated users) ---------- */
  if (phase === "suggest" && suggestion) {
    const known = SourceTagging.BOTTLED_BRANDS.find((b) => b.id === suggestion.brand);
    const display = known ? known.label : suggestion.brand || suggestion.source;
    return (
      <div style={sheet}>
        {header("Source Check", `Looks like ${display}`)}
        <p style={{ margin: "0 0 12px", fontSize: 12, color: COLORS.gray, lineHeight: 1.5 }}>
          Matched from your tagging pattern. Confirming in {countdown}s.
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => submitTag(suggestion.source, suggestion.brand, SourceTagging.ATTRIBUTION_MODES.CONFIRMED)}
            style={{
              flex: 1,
              border: "none",
              background: COLORS.blue,
              color: COLORS.white,
              fontSize: 13,
              fontWeight: 700,
              padding: "12px 10px",
              borderRadius: 999,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            That is right
          </button>
          <button
            onClick={() => {
              clearTimers();
              setPhase("grid");
            }}
            style={{
              flex: 1,
              border: `1.5px solid ${COLORS.blue}`,
              background: COLORS.white,
              color: COLORS.blue,
              fontSize: 13,
              fontWeight: 700,
              padding: "12px 10px",
              borderRadius: 999,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  /* ---------- brand drawer ---------- */
  if (phase === "drawer") {
    return (
      <div style={sheet}>
        {header("Bottled Water", "Which brand is in your bottle?")}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {SourceTagging.BOTTLED_BRANDS.map((b) => (
            <button key={b.id} onClick={() => submitTag("Bottled", b.id, SourceTagging.ATTRIBUTION_MODES.MANUAL)} style={pill(false)}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.ink }}>{b.label}</div>
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <input
            value={otherBrand}
            onChange={(e) => setOtherBrand(e.target.value)}
            placeholder={SourceTagging.MORE_BRANDS.label + "..."}
            style={{
              flex: 1,
              border: "1.5px solid #E4E7EA",
              borderRadius: 999,
              padding: "10px 14px",
              fontSize: 12.5,
              fontFamily: "inherit",
              outline: "none",
            }}
          />
          <button
            onClick={() => {
              const v = otherBrand.trim().toLowerCase();
              if (v) submitTag("Bottled", v, SourceTagging.ATTRIBUTION_MODES.MANUAL);
            }}
            disabled={!otherBrand.trim()}
            style={{
              border: "none",
              background: otherBrand.trim() ? COLORS.blue : COLORS.light,
              color: otherBrand.trim() ? COLORS.white : COLORS.gray,
              fontSize: 12.5,
              fontWeight: 700,
              padding: "10px 16px",
              borderRadius: 999,
              cursor: otherBrand.trim() ? "pointer" : "default",
              fontFamily: "inherit",
            }}
          >
            Tag
          </button>
        </div>
        <button
          onClick={() => setPhase("grid")}
          style={{
            border: "none",
            background: "transparent",
            color: COLORS.gray,
            fontSize: 11.5,
            fontWeight: 700,
            marginTop: 10,
            cursor: "pointer",
            fontFamily: "inherit",
            padding: 0,
          }}
        >
          Back to sources
        </button>
      </div>
    );
  }

  /* ---------- done: insight plus bridge ---------- */
  if (phase === "done") {
    return (
      <div style={sheet}>
        {header("Source Tagged", doneLabel)}
        {bridge ? <BridgeCard bridge={bridge} onAction={handleBridgeAction} /> : null}
        <InsightLine text={insight} />
        <button
          onClick={onClose}
          style={{
            display: "block",
            width: "100%",
            border: "none",
            background: bridge ? "transparent" : COLORS.blue,
            color: bridge ? COLORS.gray : COLORS.white,
            fontSize: 12.5,
            fontWeight: 700,
            padding: "11px 12px",
            borderRadius: 999,
            marginTop: 12,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Done
        </button>
      </div>
    );
  }

  /* ---------- full ten-pill grid (default) ---------- */
  return (
    <div style={sheet}>
      {header("Source Check", "What water did you just test?")}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {SourceTagging.SOURCE_PILLS.map((p) => (
          <button
            key={p.label}
            onClick={() =>
              p.opensBrandDrawer ? setPhase("drawer") : submitTag(p.label, null, SourceTagging.ATTRIBUTION_MODES.MANUAL)
            }
            style={pill(false)}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.ink }}>{p.label}</div>
            <div style={{ fontSize: 10, color: COLORS.gray, marginTop: 2 }}>{p.hint}</div>
          </button>
        ))}
      </div>
      <InsightLine text={SourceTagging.getInsightForCount(SourceTagging.getTagCount())} />
    </div>
  );
}
